import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Music, Image as ImageIcon, Info, X, Crop } from 'lucide-react';
import DropzoneField from './DropzoneField.tsx';
import ImageCropModal from '../ImageCropModal/ImageCropModal.tsx';
import config from '../../../config.json';
import './TuneUploadContainer.css';
import { Dayjs } from 'dayjs';
import { useDebouncedCallback } from 'use-debounce';
import { getPlaylists } from '../../../services/youtubeService.ts';
import { toastHelper } from '../../../utils/toastHelper.js'
import { UploadDefault } from '../../../services/uploadDefaultsService.ts';
import CollapsibleDrawer from './CollapsableDrawer.tsx';
import { usePermissions } from '../../../hooks/usePermissions';
import { useYouTubeData, useUploadDefaults } from '../../../contexts/AppDataContext';
import { useAuth } from '../../../components/auth/AuthContext';
import { useUserSubscription } from '../../../contexts/UserSubscriptionContext';
import { getAudioLimitsForTier } from '../../../config/audioLimitsConfig';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  RadioGroup,
  RadioGroupItem,
} from '@/components/ui/radio-group';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { DateTimePicker } from '@/components/ui/date-time-picker';

/**
 * Calculate YouTube's character count for tags.
 * YouTube counts:
 * - Tags with spaces are quoted, so "tag with spaces" = length + 2
 * - Commas between tags count toward the limit
 */
function getYouTubeTagCharCount(tags: string[]): number {
  if (tags.length === 0) return 0;

  const tagChars = tags.reduce((total, tag) => {
    // Tags with spaces get +2 for quotes
    return total + tag.length + (tag.includes(' ') ? 2 : 0);
  }, 0);

  // Add commas between tags
  const commas = tags.length - 1;

  return tagChars + commas;
}

interface TuneUploadContainerProps {
  containerIndex: number;
  trackNumber: number;
  // Controlled values (direct from parent state)
  title: string;
  description: string;
  tags: string[];
  category: string;
  playlistId: string | null;
  privacyStatus: string;
  license: string;
  madeForKids: string;
  imageResize: {
    enabled: boolean;
    horizontal_offset?: number | null;
    vertical_offset?: number | null;
  } | null;
  disableWatermark: string;
  audioFile: File | null;
  imageFile: File | null;
  uploadDate: Dayjs | null;
  isScheduled: boolean;
  // Handlers
  onChange: (index: number, updatedValues: any) => void;
  onDropAudio: (index: number, files: File[]) => void;
  onDropImage: (index: number, files: File[]) => void;
  onUploadDateChange: (index: number, date: Dayjs | null) => void;
  onIsScheduledChange: (index: number, value: boolean) => void;
  onRemove?: (index: number) => void;
  // Other
  errors: any;
  resetSignal: number;
  canDelete?: boolean;
}

function TuneUploadContainer({
  containerIndex,
  trackNumber,
  title,
  description,
  tags,
  category,
  playlistId,
  privacyStatus,
  license,
  madeForKids,
  imageResize,
  disableWatermark,
  audioFile,
  imageFile,
  uploadDate,
  isScheduled,
  onChange,
  onDropAudio,
  onDropImage,
  onUploadDateChange,
  onIsScheduledChange,
  onRemove,
  errors = {},
  resetSignal,
  canDelete = false
}: TuneUploadContainerProps) {
  // UI-only state (not form data)
  const [categories, setCategories] = useState<{ id: string, name: string }[]>([]);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [playlists, setPlaylists] = useState<{ id: string, name: string }[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_playlistSelectOpen, setPlaylistSelectOpen] = useState(false);
  const [tagLimitExceeded, setTagLimitExceeded] = useState(false);
  const [uploadDefaults, setUploadDefaults] = useState<UploadDefault[]>([]);
  const [selectedDefaultId, setSelectedDefaultId] = useState<string>('');
  const [cropModalOpen, setCropModalOpen] = useState<boolean>(false);
  const [autoResizeInfoOpen, setAutoResizeInfoOpen] = useState<boolean>(false);
  const { hasFeature, isLoading: permissionsLoading } = usePermissions();
  const { isAuthenticated } = useAuth();
  const { youtubeData, fetchYouTubeData } = useYouTubeData();
  const { uploadDefaults: contextUploadDefaults, fetchUploadDefaults } = useUploadDefaults();
  const { subscriptionStatus } = useUserSubscription();

  const tagInputRef = useRef<HTMLInputElement>(null);
  const prevSelectedDefaultIdRef = useRef<string | null>(null);
  
  // Check if user can schedule uploads
  const canScheduleUploads = hasFeature('scheduled_uploads');
  const canAutoResizeImage = hasFeature('auto_resize_image');
  const canCropImage = hasFeature('crop_image');
  const canDisableWatermark = hasFeature('watermark_optional');

  // Maximum date for scheduled uploads (30 days from now)
  const maxScheduleDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date;
  }, []);

  // Mutual exclusivity state helpers
  const isAutoResizeEnabled = imageResize?.enabled &&
    (imageResize?.horizontal_offset === null || imageResize?.horizontal_offset === undefined) &&
    (imageResize?.vertical_offset === null || imageResize?.vertical_offset === undefined);
  const isCropEnabled = imageResize?.enabled &&
    ((imageResize?.horizontal_offset !== null && imageResize?.horizontal_offset !== undefined) ||
     (imageResize?.vertical_offset !== null && imageResize?.vertical_offset !== undefined));

  // Get tier-based audio limits
  const userTier = subscriptionStatus?.subscription_tier || 'free';
  const audioLimits = useMemo(() => getAudioLimitsForTier(userTier), [userTier]);

  const imageConfig = config.fileUpload.image;

  // Fetch YouTube data on mount (explicit)
  // Only fetch if data is null (not empty object from 401 error)
  useEffect(() => {
    if (youtubeData === null && isAuthenticated) {
      fetchYouTubeData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]); // Re-fetch when authentication status changes

  // Fetch upload defaults (explicit)
  // Wait for permissions to load, then fetch if user has permission and data is null
  useEffect(() => {
    if (!isAuthenticated || permissionsLoading) {
      return; // Wait for auth and permissions
    }
    
    // Only fetch if data is null (not empty array from 403 error) and user has permission
    if (contextUploadDefaults === null && hasFeature('upload_defaults')) {
      fetchUploadDefaults();
    }
  }, [isAuthenticated, permissionsLoading, contextUploadDefaults, hasFeature, fetchUploadDefaults]);

  // Update categories and playlists from context
  useEffect(() => {
    if (youtubeData) {
      const loadedCategories = youtubeData.categories ?? [];
      setCategories(loadedCategories);
      setPlaylists(youtubeData.playlists);
      setNextPageToken(youtubeData.playlistsNextPageToken ?? null);
      
      // Default category to Music (ID "10") when YouTube data loads, if category is empty
      const hasMusicCategory = loadedCategories.some(cat => cat.id === '10');
      if (!category && hasMusicCategory) {
        onChange(containerIndex, { category: '10' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [youtubeData, category, containerIndex]);

  // Update upload defaults from context (only if user has permission)
  useEffect(() => {
    if (hasFeature('upload_defaults') && contextUploadDefaults) {
      // Use full data directly from context (no mapping needed)
      setUploadDefaults(contextUploadDefaults);
    } else {
      setUploadDefaults([]);
    }
  }, [contextUploadDefaults, hasFeature]);

  // Reset crop offset when image changes
  const prevImageFileRef = useRef<File | null>(null);
  useEffect(() => {
    if (imageFile && prevImageFileRef.current !== imageFile) {
      // Reset offsets but keep enabled state when image file actually changes
      if (imageResize?.horizontal_offset !== undefined || imageResize?.vertical_offset !== undefined) {
        const newImageResize = {
          enabled: imageResize.enabled,
          horizontal_offset: undefined,
          vertical_offset: undefined
        };
        onChange(containerIndex, { imageResize: newImageResize });
      }
    }
    prevImageFileRef.current = imageFile;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageFile, imageResize?.horizontal_offset, imageResize?.vertical_offset]);

  useEffect(() => {
    if (selectedDefaultId === '' || selectedDefaultId === '__clear__') {
      // Only reset fields if we're explicitly clearing a selected default (not on initial mount)
      const isExplicitClear = prevSelectedDefaultIdRef.current !== null && 
                              prevSelectedDefaultIdRef.current !== '' && 
                              prevSelectedDefaultIdRef.current !== '__clear__';

      setSelectedDefaultId('');
      
      if (isExplicitClear) {
        onChange(containerIndex, {
          title: '',
          description: '',
          tags: [],
          category: '',
          privacyStatus: 'private',
          license: 'youtube',
          madeForKids: 'no',
          playlistId: '',
          disableWatermark: canDisableWatermark ? 'yes' : 'no'
        });
      }
      
      prevSelectedDefaultIdRef.current = '';
      return;
    }
  
    const preset = uploadDefaults.find(d => String(d.id) === selectedDefaultId);
    if (preset) {
      onChange(containerIndex, {
        title: preset.video_title || '',
        description: preset.video_description || '',
        tags: preset.tags || [],
        category: preset.category || '',
        privacyStatus: preset.privacy_status || 'private',
        license: preset.license || 'youtube',
        madeForKids: preset.made_for_kids ? 'yes' : 'no',
        playlistId: preset.playlist_id || '',
        disableWatermark: preset.disable_watermark !== false ? 'yes' : 'no'
      });
    }
    
    prevSelectedDefaultIdRef.current = selectedDefaultId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDefaultId, uploadDefaults, canDisableWatermark, containerIndex]);

  const handleTagKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const input = tagInputRef.current?.value.trim();
      if (input) {
        const inputTags = input.split(',').map(t => t.trim()).filter(t => t && !tags.includes(t));
        const newTags = [...tags, ...inputTags];
        const newCharCount = getYouTubeTagCharCount(newTags);

        if (newCharCount <= 500) {
          onChange(containerIndex, { tags: newTags });
          setTagLimitExceeded(false);
        } else {
          setTagLimitExceeded(true);
          toastHelper.newMessage(
            'error',
            'Tag Limit Exceeded',
            'Total tags length cannot exceed 500 characters (YouTube counts spaces in tags as +2 for quotes).'
          );

          setTimeout(() => setTagLimitExceeded(false), 1500);
        }

        if (tagInputRef.current) tagInputRef.current.value = '';
      }
    }
  }, [tags, containerIndex, onChange]);

  const handleTagDelete = useCallback((tagToDelete: string) => {
    onChange(containerIndex, { tags: tags.filter(tag => tag !== tagToDelete) });
  }, [tags, containerIndex, onChange]);

  const handleLoadMorePlaylists = useCallback(async () => {
    if (!nextPageToken) return;
    try {
      const response = await getPlaylists(nextPageToken);
      setPlaylists(prev => [...prev, ...response.playlists]);
      setNextPageToken(response.nextPageToken || null);
    } catch (err) {
      toastHelper.newMessage('error', 'Playlist Load Failed', 'Could not fetch more playlists.');
    }
  }, [nextPageToken]);

  const handlePlaylistChange = useCallback((value: string) => {
    if (value === '__LOAD_MORE__') {
      handleLoadMorePlaylists();
      return;
    }
    onChange(containerIndex, { playlistId: value });
  }, [handleLoadMorePlaylists, containerIndex, onChange]);

  return (
    <Card
      className="mb-4 max-w-4xl w-full mx-auto relative"
      data-upload-mode={isScheduled ? "scheduled" : "instant"}
      data-track-index={containerIndex}
    >
      <CardContent className="space-y-4 pt-6">
      
      {/* Delete button - positioned absolutely in top right */}
      {/* Always reserve space for button to prevent layout shifts */}
      <div className="absolute top-4 right-4 h-8 w-8">
        {canDelete && onRemove && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onRemove(containerIndex)}
            className="h-8 w-8 text-muted-foreground hover:text-destructive z-10"
            title="Remove this track"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
        <div className="flex items-center gap-3">
          <span className={cn(
            "inline-flex items-center justify-center h-7 w-7 rounded-full text-sm font-bold shrink-0",
            isScheduled ? "bg-teal-500/10 text-teal-500" : "bg-primary/10 text-primary"
          )}>
            {trackNumber}
          </span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={isScheduled}
                    onCheckedChange={(checked) => {
                      if (canScheduleUploads) {
                        onIsScheduledChange(containerIndex, checked);
                      } else {
                        toastHelper.newMessage(
                          'info',
                          'Upgrade Required',
                          'Scheduled uploads require Starter or Pro subscription. Upgrade to enable this feature.'
                        );
                      }
                    }}
                    disabled={!canScheduleUploads}
                    id={`scheduled-${containerIndex}`}
                    className={cn(
                      "data-[state=unchecked]:bg-gradient-to-r data-[state=unchecked]:from-[#6a1b9a] data-[state=unchecked]:via-[#7b2fa8] data-[state=unchecked]:to-[#6a1b9a]",
                      isScheduled 
                        ? "data-[state=checked]:bg-secondary" 
                        : "data-[state=checked]:bg-primary",
                      !canScheduleUploads && "opacity-50 cursor-not-allowed"
                    )}
                  />
                  <Label 
                    htmlFor={`scheduled-${containerIndex}`} 
                    className={cn(
                      "text-sm font-medium leading-none",
                      canScheduleUploads ? "cursor-pointer" : "cursor-not-allowed opacity-50"
                    )}
                  >
                    {isScheduled ? "Scheduled Upload" : "Instant Upload"}
                  </Label>
                </div>
              </TooltipTrigger>
              {!canScheduleUploads && (
                <TooltipContent>
                  <p>Upgrade to Starter or Pro to enable scheduled uploads</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>

        {hasFeature('upload_defaults') && (
          <div className="w-full sm:w-[220px] sm:max-w-[300px]">
            <Label className="text-xs mb-1 block">Upload Default</Label>
            <Select value={selectedDefaultId || ''} onValueChange={(value) => setSelectedDefaultId(value)}>
            <SelectTrigger className={cn(
              "border-0 focus:ring-0 focus:ring-offset-0",
              isScheduled && "focus:ring-secondary",
              !isScheduled && "focus:ring-primary"
            )}>
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__clear__">None</SelectItem>
              {uploadDefaults.map((d) => (
                <SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          </div>
        )}
      </div>

      {/* Two-column layout */}
      <div className="flex flex-col md:flex-row gap-4 items-stretch pb-2">
        {/* Left Column */}
        <div className="flex-1 flex flex-col">
          {/* Dropzone Audio section */}
          <div className="h-[120px]">
            <DropzoneField
              onDrop={(files) => onDropAudio(containerIndex, files)}
              label={
                <div className="flex flex-col items-center text-center">
                  <Music className={cn(
                    "h-6 w-6 mb-2", 
                    audioFile 
                      ? (isScheduled ? "text-secondary" : "text-primary")
                      : "text-muted-foreground"
                  )} />
                  <p className="text-sm text-muted-foreground">Drag and drop audio file here</p>
                </div>
              }
              file={audioFile}
              acceptedFileTypes={audioLimits.accept}
              maxFileSize={audioLimits.maxFileSizeBytes}
              isValid={!errors.audio}
              setIsValid={() => {}}
              isScheduled={isScheduled}
              tierDisplayFormats={audioLimits.displayFormats}
            />
            <div className="mt-2 h-8">
              {audioFile && (
                <Button variant="ghost" size="sm" onClick={() => onDropAudio(containerIndex, [])} className="text-destructive hover:text-destructive">
                  Clear Audio
                </Button>
              )}
            </div>
          </div>
          
          {/* Dropzone Image section - appears right after audio on mobile */}
          <div className="h-[120px] md:hidden mt-4">
            <DropzoneField
              onDrop={(files) => onDropImage(containerIndex, files)}
              label={
                <div className="flex flex-col items-center text-center">
                  <ImageIcon className={cn(
                    "h-6 w-6 mb-2", 
                    imageFile 
                      ? (isScheduled ? "text-secondary" : "text-primary")
                      : "text-muted-foreground"
                  )} />
                  <p className="text-sm text-muted-foreground">Drag and drop image file here</p>
                </div>
              }
              file={imageFile}
              acceptedFileTypes={imageConfig.accept}
              maxFileSize={imageConfig.maxFileSize}
              isValid={!errors.image}
              setIsValid={() => {}}
              isScheduled={isScheduled}
            />
            <div className="flex items-center mt-2 h-8">
              {imageFile ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDropImage(containerIndex, [])}
                  className="text-destructive hover:text-destructive"
                >
                  Clear Image
                </Button>
              ) : (
                <div className="w-24" />
              )}

              <div className="flex-1" />

              {/* Desktop: Tooltip on hover */}
              {canAutoResizeImage && (
                <div className="hidden md:block">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                        >
                          <Info className={cn(
                            "h-4 w-4",
                            imageResize?.enabled 
                              ? (isScheduled ? "text-secondary" : "text-primary")
                              : "text-muted-foreground"
                          )} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[360px] space-y-2">
                        <p>
                          {canAutoResizeImage
                            ? (imageResize?.enabled
                                ? 'Auto-resize is enabled. Image is resized to 1080x1080 square, letterboxed on 1920x1080 with black bars (no stretch).'
                                : 'Enable auto-resize to letterbox the image: resize to 1080x1080 square and pad to 1920x1080 with black bars (no stretch).')
                            : 'Auto-resize letterboxes to 1920x1080 (1080x1080 square inner, black bars). Upgrade to Starter/Pro to enable.'}
                        </p>
                        <img
                          src="/auto-resize-letterbox.png"
                          alt="Letterbox: 1080x1080 square inside 1920x1080 with black bars"
                          className="w-full rounded border border-muted"
                        />
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}

              {/* Mobile: Popover on click */}
              {canAutoResizeImage && (
                <div className="block md:hidden">
                  <Popover open={autoResizeInfoOpen} onOpenChange={setAutoResizeInfoOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setAutoResizeInfoOpen(!autoResizeInfoOpen);
                        }}
                      >
                        <Info className={cn(
                          "h-4 w-4",
                          imageResize?.enabled 
                            ? (isScheduled ? "text-secondary" : "text-primary")
                            : "text-muted-foreground"
                        )} />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent side="bottom" className="max-w-[360px] space-y-2" onOpenAutoFocus={(e) => e.preventDefault()}>
                      <p>
                        {canAutoResizeImage
                          ? (imageResize?.enabled
                              ? 'Auto-resize is enabled. Image is resized to 1080x1080 square, letterboxed on 1920x1080 with black bars (no stretch).'
                              : 'Enable auto-resize to letterbox the image: resize to 1080x1080 square and pad to 1920x1080 with black bars (no stretch).')
                          : 'Auto-resize letterboxes to 1920x1080 (1080x1080 square inner, black bars). Upgrade to Starter/Pro to enable.'}
                      </p>
                      <img
                        src="/auto-resize-letterbox.png"
                        alt="Letterbox: 1280x720 inside 1920x1080 with black bars"
                        className="w-full rounded border border-muted"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {canAutoResizeImage && (
                <Button
                  variant={isAutoResizeEnabled ? "default" : "outline"}
                  size="sm"
                  disabled={isCropEnabled}
                  onClick={() => {
                    if (isCropEnabled) {
                      return;
                    }
                    const newEnabled = !isAutoResizeEnabled;
                    const newImageResize = {
                      enabled: newEnabled,
                      horizontal_offset: newEnabled ? null : undefined
                    };
                    onChange(containerIndex, { imageResize: newImageResize });
                  }}
                  className={cn(
                    "ml-2",
                    isAutoResizeEnabled && !isScheduled && "!bg-primary hover:!bg-primary/90 !text-primary-foreground",
                    isAutoResizeEnabled && isScheduled && "!bg-secondary hover:!bg-secondary/90 !text-secondary-foreground",
                    isCropEnabled && "opacity-50 cursor-not-allowed"
                  )}
                >
                  Auto-Resize
                </Button>
              )}
              {imageFile && canCropImage && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isAutoResizeEnabled}
                  onClick={() => {
                    if (isAutoResizeEnabled) {
                      return;
                    }
                    setCropModalOpen(true);
                  }}
                  className={cn(
                    "ml-2",
                    isAutoResizeEnabled && "opacity-50 cursor-not-allowed"
                  )}
                >
                  <Crop className="h-4 w-4 mr-1" />
                  Crop Image
                </Button>
              )}
            </div>
          </div>

          {/* Title and Description section - aligned with Add Tags */}
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label htmlFor={`title-${containerIndex}`}>YouTube Video Title</Label>
              <Input
                id={`title-${containerIndex}`}
                value={title}
                onChange={(e) => onChange(containerIndex, { title: e.target.value })}
                className={cn(
                  "border-0 focus-visible:ring-0 focus-visible:ring-offset-0",
                  errors.title && "border-destructive border",
                  !errors.title && !isScheduled && "focus-visible:border-2 focus-visible:border-primary",
                  !errors.title && isScheduled && "focus-visible:border-2 focus-visible:border-secondary"
                )}
              />
              {errors.title && (
                <p className="text-sm text-destructive">{errors.title}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor={`description-${containerIndex}`}>YouTube Video Description</Label>
              <Textarea
                id={`description-${containerIndex}`}
                value={description}
                onChange={(e) => onChange(containerIndex, { description: e.target.value })}
                rows={8}
                className={cn(
                  "resize-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0",
                  !isScheduled && "focus-visible:border-2 focus-visible:border-primary",
                  isScheduled && "focus-visible:border-2 focus-visible:border-secondary"
                )}
              />
            </div>
          </div> 
        </div>
            
        {/* Right Column */}
        <div className="flex-1 flex flex-col">
          {/* Dropzone Image section - hidden on mobile, shown on desktop */}
          <div className="h-[120px] hidden md:block">
            <DropzoneField
              onDrop={(files) => onDropImage(containerIndex, files)}
              label={
                <div className="flex flex-col items-center text-center">
                  <ImageIcon className={cn(
                    "h-6 w-6 mb-2", 
                    imageFile 
                      ? (isScheduled ? "text-secondary" : "text-primary")
                      : "text-muted-foreground"
                  )} />
                  <p className="text-sm text-muted-foreground">Drag and drop image file here</p>
                </div>
              }
              file={imageFile}
              acceptedFileTypes={imageConfig.accept}
              maxFileSize={imageConfig.maxFileSize}
              isValid={!errors.image}
              setIsValid={() => {}}
              isScheduled={isScheduled}
            />
            <div className="flex items-center mt-2 h-8">
              {imageFile ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDropImage(containerIndex, [])}
                  className="text-destructive hover:text-destructive"
                >
                  Clear Image
                </Button>
              ) : (
                <div className="w-24" />
              )}

              <div className="flex-1" />

              {/* Desktop: Tooltip on hover, Mobile: Popover on click */}
              {canAutoResizeImage && (
                <div className="hidden md:block">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                        >
                          <Info className={cn(
                            "h-4 w-4",
                            imageResize?.enabled 
                              ? (isScheduled ? "text-secondary" : "text-primary")
                              : "text-muted-foreground"
                          )} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[360px] space-y-2">
                        <p>
                          {canAutoResizeImage
                            ? (imageResize?.enabled
                                ? 'Auto-resize is enabled. Image is resized to 1080x1080 square, letterboxed on 1920x1080 with black bars (no stretch).'
                                : 'Enable auto-resize to letterbox the image: resize to 1080x1080 square and pad to 1920x1080 with black bars (no stretch).')
                            : 'Auto-resize letterboxes to 1920x1080 (1080x1080 square inner, black bars). Upgrade to Starter/Pro to enable.'}
                        </p>
                        <img
                          src="/auto-resize-letterbox.png"
                          alt="Letterbox: 1080x1080 square inside 1920x1080 with black bars"
                          className="w-full rounded border border-muted"
                        />
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}

              {canAutoResizeImage && (
                <Button
                  variant={isAutoResizeEnabled ? "default" : "outline"}
                  size="sm"
                  disabled={isCropEnabled}
                  onClick={() => {
                    if (isCropEnabled) {
                      return;
                    }
                    const newEnabled = !isAutoResizeEnabled;
                    const newImageResize = {
                      enabled: newEnabled,
                      horizontal_offset: newEnabled ? null : undefined
                    };
                    onChange(containerIndex, { imageResize: newImageResize });
                  }}
                  className={cn(
                    "ml-2",
                    isAutoResizeEnabled && !isScheduled && "!bg-primary hover:!bg-primary/90 !text-primary-foreground",
                    isAutoResizeEnabled && isScheduled && "!bg-secondary hover:!bg-secondary/90 !text-secondary-foreground",
                    isCropEnabled && "opacity-50 cursor-not-allowed"
                  )}
                >
                  Auto-Resize
                </Button>
              )}
              {imageFile && canCropImage && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isAutoResizeEnabled}
                  onClick={() => {
                    if (isAutoResizeEnabled) {
                      return;
                    }
                    setCropModalOpen(true);
                  }}
                  className={cn(
                    "ml-2",
                    isAutoResizeEnabled && "opacity-50 cursor-not-allowed"
                  )}
                >
                  <Crop className="h-4 w-4 mr-1" />
                  Crop Image
                </Button>
              )}
            </div>
          </div>
          {/* Entire Tags Section - aligned with YouTube Video Title */}
          <div className="space-y-2 mt-2">
            <Label htmlFor={`tags-${containerIndex}`}>Add Tags (Enter or comma-separated)</Label>
            <Input
              id={`tags-${containerIndex}`}
              ref={tagInputRef}
              onKeyDown={handleTagKeyDown}
              className={cn(
                "border-0 focus-visible:ring-0 focus-visible:ring-offset-0",
                tagLimitExceeded && "border-destructive border",
                !tagLimitExceeded && !isScheduled && "focus-visible:border-2 focus-visible:border-primary",
                !tagLimitExceeded && isScheduled && "focus-visible:border-2 focus-visible:border-secondary"
              )}
            />
            {tagLimitExceeded && (
              <p className="text-sm text-destructive">Max total tag length is 500 characters</p>
            )}
          </div>
          {/* Tags Counter and Wrapper section*/}
          <div className="mt-3">
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm font-semibold">Tags ({tags.length})</span>
              <div className="flex items-center gap-2">
                <span className={cn(
                  "text-xs",
                  getYouTubeTagCharCount(tags) >= 490
                    ? "text-destructive"
                    : getYouTubeTagCharCount(tags) >= 450
                    ? "text-orange-500"
                    : "text-muted-foreground"
                )}>
                  {getYouTubeTagCharCount(tags)}/500
                </span>
                {tags.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onChange(containerIndex, { tags: [] })}
                    className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                  >
                    Clear All
                  </Button>
                )}
              </div>
            </div>
            <div className="tags-wrapper">
              {tags.map((tag, idx) => (
                <TooltipProvider key={idx}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="outline"
                        className={cn(
                          "cursor-pointer max-w-full px-1.5 py-0.5 text-[10px] font-normal h-5 shrink-0",
                          !isScheduled 
                            ? "border-purple-500/50 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20"
                            : "border-secondary/50 bg-secondary/10 text-secondary hover:bg-secondary/20"
                        )}
                      >
                        <span className="truncate max-w-[200px] sm:max-w-none">{tag}</span>
                        <button
                          onClick={() => handleTagDelete(tag)}
                          className={cn(
                            "ml-1 hover:text-destructive shrink-0",
                            !isScheduled && "hover:bg-purple-500/30",
                            isScheduled && "hover:bg-secondary/30"
                          )}
                          aria-label={`Remove ${tag}`}
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{tag}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            </div>
          </div>
          {/* Privacy status section*/}
          <div className="mt-4 space-y-2 min-h-[60px]">
            <Label>Privacy Status</Label>
            <RadioGroup value={privacyStatus} onValueChange={(value) => onChange(containerIndex, { privacyStatus: value })} className="flex flex-col md:flex-row gap-4">
              <div className="flex items-center space-x-2">
                <RadioGroupItem 
                  value="public" 
                  id={`privacy-public-${containerIndex}`}
                  disabled={isScheduled}
                  className={cn(
                    !isScheduled 
                      ? "!border-primary !text-primary focus-visible:ring-primary" 
                      : "!border-secondary !text-secondary focus-visible:ring-secondary"
                  )}
                />
                <Label 
                  htmlFor={`privacy-public-${containerIndex}`} 
                  className={cn(
                    "font-normal",
                    isScheduled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                  )}
                >
                  Public
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem 
                  value="private" 
                  id={`privacy-private-${containerIndex}`}
                  className={cn(
                    !isScheduled 
                      ? "!border-primary !text-primary focus-visible:ring-primary" 
                      : "!border-secondary !text-secondary focus-visible:ring-secondary"
                  )}
                />
                <Label htmlFor={`privacy-private-${containerIndex}`} className="cursor-pointer font-normal">Private</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem 
                  value="unlisted" 
                  id={`privacy-unlisted-${containerIndex}`}
                  disabled={isScheduled}
                  className={cn(
                    !isScheduled 
                      ? "!border-primary !text-primary focus-visible:ring-primary" 
                      : "!border-secondary !text-secondary focus-visible:ring-secondary"
                  )}
                />
                <Label 
                  htmlFor={`privacy-unlisted-${containerIndex}`} 
                  className={cn(
                    "font-normal",
                    isScheduled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                  )}
                >
                  Unlisted
                </Label>
              </div>
            </RadioGroup>
          </div>       
        </div>
      </div>
      {/* Upload Settings Collapsable Drawer section*/}
      <div className="mt-6 mb-6 w-full">
        <CollapsibleDrawer title="Upload Settings" isScheduled={isScheduled}>
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 w-full">
            <div className="space-y-2">
              <Label>License</Label>
              <RadioGroup value={license} onValueChange={(value) => onChange(containerIndex, { license: value })} className="flex flex-row gap-4">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="youtube"
                    id={`license-youtube-${containerIndex}`}
                    className={cn(
                      !isScheduled
                        ? "!border-primary !text-primary focus-visible:ring-primary"
                        : "!border-secondary !text-secondary focus-visible:ring-secondary"
                    )}
                  />
                  <Label htmlFor={`license-youtube-${containerIndex}`} className="cursor-pointer font-normal">YouTube</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="creativeCommon"
                    id={`license-cc-${containerIndex}`}
                    className={cn(
                      !isScheduled
                        ? "!border-primary !text-primary focus-visible:ring-primary"
                        : "!border-secondary !text-secondary focus-visible:ring-secondary"
                    )}
                  />
                  <Label htmlFor={`license-cc-${containerIndex}`} className="cursor-pointer font-normal whitespace-nowrap">Creative Commons</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label>Made for Kids</Label>
              <RadioGroup value={madeForKids} onValueChange={(value) => onChange(containerIndex, { madeForKids: value })} className="flex flex-row gap-4">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="yes"
                    id={`kids-yes-${containerIndex}`}
                    className={cn(
                      !isScheduled
                        ? "!border-primary !text-primary focus-visible:ring-primary"
                        : "!border-secondary !text-secondary focus-visible:ring-secondary"
                    )}
                  />
                  <Label htmlFor={`kids-yes-${containerIndex}`} className="cursor-pointer font-normal">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="no"
                    id={`kids-no-${containerIndex}`}
                    className={cn(
                      !isScheduled
                        ? "!border-primary !text-primary focus-visible:ring-primary"
                        : "!border-secondary !text-secondary focus-visible:ring-secondary"
                    )}
                  />
                  <Label htmlFor={`kids-no-${containerIndex}`} className="cursor-pointer font-normal">No</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Remove Watermark - Visible for all users, Yes is disabled for free users */}
            <div className="space-y-2">
              <Label>Remove Watermark</Label>
              <RadioGroup value={disableWatermark} onValueChange={(value) => onChange(containerIndex, { disableWatermark: value })} className="flex flex-row gap-4">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="yes"
                    id={`watermark-yes-${containerIndex}`}
                    disabled={!canDisableWatermark}
                    className={cn(
                      !isScheduled
                        ? "!border-primary !text-primary focus-visible:ring-primary"
                        : "!border-secondary !text-secondary focus-visible:ring-secondary",
                      !canDisableWatermark && "opacity-50 cursor-not-allowed"
                    )}
                  />
                  <Label
                    htmlFor={`watermark-yes-${containerIndex}`}
                    className={cn(
                      "font-normal",
                      canDisableWatermark ? "cursor-pointer" : "cursor-not-allowed opacity-50"
                    )}
                  >
                    Yes
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="no"
                    id={`watermark-no-${containerIndex}`}
                    className={cn(
                      !isScheduled
                        ? "!border-primary !text-primary focus-visible:ring-primary"
                        : "!border-secondary !text-secondary focus-visible:ring-secondary"
                    )}
                  />
                  <Label htmlFor={`watermark-no-${containerIndex}`} className="cursor-pointer font-normal">No</Label>
                </div>
              </RadioGroup>
            </div>
          </div>
          <div className="flex gap-3 flex-col md:flex-row mt-4">
            <div className="flex-1 space-y-2 mb-2">
              <Label htmlFor={`category-${containerIndex}`}>Category</Label>
              <Select value={category} onValueChange={(value) => onChange(containerIndex, { category: value })} disabled={!isAuthenticated}>
                <SelectTrigger className={cn(
                  "border-0 focus:ring-0 focus:ring-offset-0",
                  errors.category && "border-destructive",
                  !errors.category && isScheduled && "focus:ring-secondary",
                  !errors.category && !isScheduled && "focus:ring-primary",
                  !isAuthenticated && "opacity-50 cursor-not-allowed"
                )}>
                  <SelectValue placeholder={isAuthenticated ? "Select category" : "Sign in to select"} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.category && (
                <p className="text-sm text-destructive">{errors.category}</p>
              )}
            </div>

            <div className="flex-1 space-y-2">
              <Label htmlFor={`playlist-${containerIndex}`}>Playlist</Label>
              <Select
                value={playlistId || undefined}
                onValueChange={handlePlaylistChange}
                disabled={!isAuthenticated || playlists.length === 0}
                onOpenChange={setPlaylistSelectOpen}
              >
                <SelectTrigger className={cn(
                  "border-0 focus:ring-0 focus:ring-offset-0",
                  isScheduled && "focus:ring-secondary",
                  !isScheduled && "focus:ring-primary",
                  !isAuthenticated && "opacity-50 cursor-not-allowed"
                )}>
                  <SelectValue placeholder={isAuthenticated ? "None" : "Sign in to select"} />
                </SelectTrigger>
                <SelectContent>
                  {playlists.map(pl => (
                    <SelectItem key={pl.id} value={pl.id}>{pl.name}</SelectItem>
                  ))}
                  {nextPageToken && (
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleLoadMorePlaylists();
                      }}
                      className="px-2 py-1.5 text-sm italic text-muted-foreground cursor-pointer hover:bg-accent rounded-sm"
                    >
                      Load more…
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CollapsibleDrawer>
      </div>
      {/* Scheduled Upload Date Time Picker section*/}
      <div className={cn(
        "transition-all duration-300 ease-in-out overflow-hidden",
        isScheduled && uploadDate !== undefined
          ? "max-h-96 opacity-100 translate-y-0 mt-6 mb-4"
          : "max-h-0 opacity-0 -translate-y-4 mt-0 mb-0"
      )}>
        <DateTimePicker
          label="Upload Date and Time"
          value={uploadDate || null}
          onChange={(date) => onUploadDateChange(containerIndex, date)}
          error={Boolean(errors?.uploadDate)}
          helperText={errors?.uploadDate}
          className="w-full"
          disablePastDates={true}
          maxDate={maxScheduleDate}
          isScheduled={true}
        />
      </div>
      </CardContent>
      
      {/* Image Crop Modal */}
      {imageFile && (
        <ImageCropModal
          imageFile={imageFile}
          open={cropModalOpen}
          onApply={(horizontalOffset, verticalOffset) => {
            const newImageResize = {
              enabled: true,
              horizontal_offset: horizontalOffset ?? undefined,
              vertical_offset: verticalOffset ?? undefined
            };
            onChange(containerIndex, { imageResize: newImageResize });
            setCropModalOpen(false);
          }}
          onCancel={() => setCropModalOpen(false)}
        />
      )}
    </Card>
  );
}

export default TuneUploadContainer;